import { FLAGS } from '@gozoki/feature-flags'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useContext } from 'react'
import RbacAccessDeniedPage from '~/components/communication/RbacAccessDeniedPage'
import { LogisticsTestPage } from '~/components/routes/logistics/logistics-test-page'
import { useFeatureFlag, useFeatureFlagsReady } from '~/hooks/useFeatureFlag'
import { AuthContext } from '~/utils/context/auth.context'
import {
  useMigratedRight,
  useMissingPermissions,
  withRoutePermissions,
  type PermissionRequirement,
} from '~/utils/rbac'

// La page porte les six sections du module en un seul écran : son exigence est donc l'union
// des lectures qu'elles demandent, et non la seule simulation — sinon un profil cantonné aux
// référentiels perdrait l'entrée de menu et la page, alors que ses sections lui répondraient.
// Chaque section pose ensuite sa propre garde côté API.
const LOGISTICS_READ: PermissionRequirement = {
  any: [
    ['read', 'logistics.referentiels.carrier'],
    ['read', 'logistics.referentiels.vehicle'],
    ['read', 'logistics.referentiels.hub'],
    ['read', 'logistics.referentiels.driver'],
    ['read', 'logistics.referentiels.store-profile'],
    ['read', 'logistics.simulation.simulation'],
  ],
}

export const Route = createFileRoute('/_authenticated/logistics')({
  staticData: {
    permission: LOGISTICS_READ,
  },
  component: LogisticsRoute,
})

withRoutePermissions(Route)

function LogisticsRoute() {
  const { isLoading } = useContext(AuthContext)
  const featureEnabled = useFeatureFlag(FLAGS.logistique)
  const flagsReady = useFeatureFlagsReady()
  // Tant que `permission-v2` est OFF, `withRoutePermissions` laisse tout passer : le bit
  // `LOGISTICS` (celui qui cache l'entrée de menu) reste la garde effective de la page, sans quoi
  // un admin sans ce droit l'ouvrirait par URL et n'y verrait que des 403.
  const canRead = useMigratedRight(LOGISTICS_READ, ['LOGISTICS'])
  const missingPermissions = useMissingPermissions(LOGISTICS_READ)

  if (isLoading || !flagsReady) {
    return <p className="p-4 text-sm text-muted-foreground">Chargement…</p>
  }

  if (!featureEnabled) {
    return <Navigate to="/" replace />
  }

  if (!canRead) {
    return <RbacAccessDeniedPage missingPermissions={missingPermissions} />
  }

  return <LogisticsTestPage />
}
